import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { adminChangePassword } from "../lib/api";

const MIN_LEN = 8;

export default function ChangePasswordDialog() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setShow(false);
    setBusy(false);
  };

  // Reset fields whenever the dialog is closed, regardless of which path
  // closed it (X button, ESC, overlay click, OR the explicit Cancel button).
  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const mismatch = next.length > 0 && confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < MIN_LEN;
  const sameAsCurrent = next.length > 0 && current.length > 0 && next === current;
  const submitDisabled =
    busy ||
    !current ||
    next.length < MIN_LEN ||
    confirm !== next ||
    sameAsCurrent;

  const submit = async (e) => {
    e.preventDefault();
    if (submitDisabled) return;
    setBusy(true);
    try {
      await adminChangePassword(current, next);
      toast.success("Password changed", {
        description: "Use the new password on next sign-in.",
      });
      setOpen(false);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message;
      if (status === 401) {
        toast.error("Current password is incorrect");
      } else if (status === 400) {
        toast.error("Invalid password", { description: String(detail) });
      } else if (status !== 429) {
        toast.error("Couldn't change password", { description: String(detail) });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="admin-change-password-btn"
          className="flex items-center gap-1.5 text-xs font-mono text-slate-700 hover:text-violet-700 border border-slate-200 hover:border-violet-300 rounded-md px-3 py-1.5 transition-colors"
        >
          <KeyRound className="h-3.5 w-3.5" />
          Change password
        </button>
      </DialogTrigger>
      <DialogContent
        className="bg-white border-slate-200 text-slate-900 sm:max-w-md"
        data-testid="change-password-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Change admin password
          </DialogTitle>
          <DialogDescription className="text-slate-600 text-sm">
            Minimum {MIN_LEN} characters. You stay signed in on this browser.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <Field
            label="Current password"
            value={current}
            onChange={setCurrent}
            show={show}
            autoFocus
            testid="cp-current"
          />
          <Field
            label="New password"
            value={next}
            onChange={setNext}
            show={show}
            testid="cp-new"
            error={
              tooShort
                ? `Min ${MIN_LEN} characters`
                : sameAsCurrent
                  ? "Must differ from current"
                  : null
            }
          />
          <Field
            label="Confirm new password"
            value={confirm}
            onChange={setConfirm}
            show={show}
            testid="cp-confirm"
            error={mismatch ? "Doesn't match" : null}
          />

          <label className="flex items-center gap-2 text-xs font-mono text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={show}
              onChange={(e) => setShow(e.target.checked)}
              className="h-3 w-3"
            />
            {show ? (
              <span className="flex items-center gap-1.5">
                <EyeOff className="h-3 w-3" /> Hide values
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <Eye className="h-3 w-3" /> Show values
              </span>
            )}
          </label>

          <DialogFooter className="gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs font-mono text-slate-600 hover:text-slate-800 border border-slate-200 rounded-md px-3 py-2 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitDisabled}
              data-testid="cp-submit"
              className="flex items-center justify-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <KeyRound className="h-3.5 w-3.5" />
              {busy ? "Changing…" : "Change password"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, show, error, autoFocus, testid }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
        data-testid={testid}
        className={`w-full bg-slate-50 border ${
          error ? "border-red-200" : "border-slate-200"
        } rounded-md px-3 py-2 font-mono text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-200`}
      />
      {error && (
        <div className="mt-1 text-[10px] font-mono text-red-700">{error}</div>
      )}
    </div>
  );
}
